import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslate } from '@/translations';
import { Alert, Form, Input, Modal, Select, Spin } from 'antd';
import { CustomerInspectors, Inspector, Location } from '@/types';

type InspectorPopupProps = {
  open: boolean;
  locations?: Location[];
  customers?: CustomerInspectors[];
  onSubmit(value: Inspector): Promise<string | undefined>;
  onCancel(): void;
};

export const InspectorPopup: FC<InspectorPopupProps> = ({
  open,
  locations,
  customers,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslate();
  const [form] = Form.useForm<Inspector>();
  const [customerLocations, setCustomerLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const handleSubmit = useCallback(
    async (value: Inspector) => {
      setErrorMessage(undefined);
      setLoading(true);
      const error = await onSubmit(value);
      setErrorMessage(error);

      setLoading(false);
      if (!error) onCancel();
    },
    [onCancel, onSubmit],
  );

  useEffect(() => {
    if (!open) return;
    setErrorMessage(undefined);
    form.resetFields();
  }, [form, open]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title="Create Inspector"
      maskClosable
      closable={false}
      okText={t('save')}
      cancelText={t('cancel')}
      onOk={() => form.submit()}
      confirmLoading={loading}
    >
      <Spin spinning={loading}>
        <Form
          id="inspector-form"
          form={form}
          onFinish={handleSubmit}
          size="large"
          labelCol={{ flex: '0 0 160px' }}
          autoComplete="off"
        >
          <Form.Item<Inspector>
            label={t('customer')}
            name="customerId"
            required
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              options={customers}
              fieldNames={{ label: 'customerName', value: 'customerId' }}
              optionFilterProp="customerName"
              onChange={(value) =>
                setCustomerLocations(
                  locations?.filter((o) =>
                    o.customers.some((c) => c.value === value),
                  ) || [],
                )
              }
            />
          </Form.Item>

          <Form.Item<Inspector>
            label={t('inspectorCode')}
            name="inspectorCode"
            required
            rules={[{ required: true }]}
          >
            <Input maxLength={4} />
          </Form.Item>

          <Form.Item<Inspector>
            label={t('inspectorForenames')}
            name="inspectorForenames"
            required
            rules={[{ required: true }]}
          >
            <Input maxLength={40} />
          </Form.Item>

          <Form.Item<Inspector>
            label={t('inspectorSurname')}
            name="inspectorSurname"
            rules={[{ required: true }]}
          >
            <Input maxLength={30} />
          </Form.Item>

          <Form.Item<Inspector> label="Site" name="siteId">
            <Select
              allowClear
              placeholder="Choose Site"
              showSearch
              optionFilterProp="label"
              options={customerLocations?.map((o) => ({
                value: o.id,
                label: o.desc,
              }))}
            />
          </Form.Item>

          {errorMessage ? (
            <Alert message={errorMessage} type="error" className="mb-4" />
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};
